import React, { useEffect, useMemo, useState } from 'react';
import { RichEditor } from '../rich-editor';
import type { RichEditorImageUploadHandler, TiptapDoc } from '../rich-editor';
import {
    applyResolvedUrlsToDoc,
    collectDocImageFileIds,
    isDocEmpty,
    RICH_BODY_TYPOGRAPHY_CLASS
} from '../rich-editor';

// Read-only render only: RichEditor never calls these while readOnly. Same
// pattern as files-viewer's NoteBody.
const NOOP_CHANGE = (): void => {};
const NOOP_UPLOAD: RichEditorImageUploadHandler = () => Promise.reject(new Error('read-only'));

/** Exchanges the file-ids of a body's images for freshly signed read URLs.
 *  Consumers must memoize the function they pass: its identity drives the
 *  resolve effect. */
export type EventImageUrlResolver = (fileIds: string[]) => Promise<Record<string, string>>;

export interface EventBodyProps {
    /** The TipTap document, as it comes off `EventDto.bodyDoc` (typed `unknown`). */
    readonly bodyDoc: unknown;
    /** Plain-text fallback for an event that has no document. */
    readonly body: string | null;
    /** Omit for a body with no uploaded images; the stored `src` values render as-is. */
    readonly resolveImageUrls?: EventImageUrlResolver;
}

// Read-only render of the authored document. Signed URLs stored in the
// document expire, so a body carrying uploaded images re-resolves them by
// file-id before rendering.
export const EventBody: React.FC<EventBodyProps> = ({ bodyDoc, body, resolveImageUrls }) => {
    const doc = useMemo(() => (bodyDoc ?? null) as TiptapDoc | null, [bodyDoc]);
    const [renderDoc, setRenderDoc] = useState<TiptapDoc | null>(doc);

    useEffect(() => {
        setRenderDoc(doc);
        if (!doc || !resolveImageUrls) return;

        const fileIds = collectDocImageFileIds(doc);
        if (fileIds.length === 0) return;

        let cancelled = false;
        resolveImageUrls(fileIds)
            .then((urls) => {
                if (cancelled || !urls) return;
                setRenderDoc(applyResolvedUrlsToDoc(doc, urls));
            })
            .catch(() => {
                // Deliberate: the stored srcs stay on screen. They may be an
                // expired signed URL, but a partially-rendered body beats
                // replacing the announcement with an error.
                if (!cancelled) setRenderDoc(doc);
            });

        return () => {
            cancelled = true;
        };
    }, [doc, resolveImageUrls]);

    const hasDoc = Boolean(renderDoc) && !isDocEmpty(renderDoc as TiptapDoc);

    if (!hasDoc) {
        if (!body || !body.trim()) return null;
        return <p className="text-sm text-gray-700 whitespace-pre-wrap">{body}</p>;
    }

    return (
        <div className={`event-rich-body ${RICH_BODY_TYPOGRAPHY_CLASS}`}>
            <RichEditor
                value={renderDoc as TiptapDoc}
                onChange={NOOP_CHANGE}
                onImageUpload={NOOP_UPLOAD}
                readOnly
                chrome="bare"
                // Authors label before/after photos; without this the labels are
                // written in the editor and silently dropped for readers.
                enableImageCaptions
            />
        </div>
    );
};
